import GhostContentAPI from "@tryghost/content-api";
import { logError } from "./logging";
import { GUIDES_GHOST_API_VERSION, BLOG_GHOST_API_VERSION, GHOST_IMAGE_SIZE } from "./constants";
import { showErrorAlert } from "./alerts";

/**
 * Old function that executes Guides Ghost API for guides posts.
 *
 * @param {String | Array} filters
 * @return {Promise}
 * */
const executeGhostApi = async (filters) => {
  const api = new GhostContentAPI({
    url: import.meta.env.REACT_APP_GUIDES_GHOST_DOMAIN,
    key: import.meta.env.REACT_APP_GUIDES_GHOST_API_KEY,
    version: GUIDES_GHOST_API_VERSION,
  });

  const filter = Array.isArray(filters) ? `tags:[${filters}]` : `tag:${filters}`;

  return api.posts
    .browse({
      include: "tags,authors",
      filter,
      limit: "all",
      order: "slug ASC",
    })
    .then((posts) => posts)
    .catch((err) => {
      logError(err);
      showErrorAlert("Sorry, something went wrong. Please try again.");
      return [];
    });
};

/**
 * New function that executes Guides Ghost API for guides posts.
 *
 * @param {String | Number} filter
 * @param {String | Number} limit
 * @return {Promise}
 * */
const executeGuidesGhostApi = async (filter, limit) => {
  const api = new GhostContentAPI({
    url: import.meta.env.REACT_APP_GUIDES_GHOST_DOMAIN,
    key: import.meta.env.REACT_APP_GUIDES_GHOST_API_KEY,
    version: GUIDES_GHOST_API_VERSION,
  });

  return api.posts
    .browse({
      include: "tags,authors",
      filter,
      limit,
    })
    .then((posts) => posts)
    .catch((err) => {
      logError(err);
      showErrorAlert("Sorry, something went wrong. Please try again.");
      return [];
    });
};

/**
 * Executes Blog Ghost API for guides posts.
 *
 * @param {String | Number} filter
 * @param {String | Number} limit
 * @return {Promise}
 * */
const executeBlogGhostApi = async (filter, limit) => {
  const api = new GhostContentAPI({
    url: import.meta.env.REACT_APP_BLOG_GHOST_DOMAIN,
    key: import.meta.env.REACT_APP_BLOG_GHOST_API_KEY,
    version: BLOG_GHOST_API_VERSION,
  });

  return api.posts
    .browse({
      include: "tags,authors",
      filter,
      limit,
    })
    .then((posts) => posts)
    .catch((err) => {
      logError(err);
      showErrorAlert("Sorry, something went wrong. Please try again.");
      return [];
    });
};

/**
 * Search Guides Ghost API using NQL filters on title and plaintext.
 * Splits the search term into individual words and requires ALL words
 * to match in either the title OR plaintext of each post.
 *
 * @param {String} searchTerm
 * @returns {Promise<Array>}
 * */
const searchGuidesGhostApi = async (searchTerm) => {
  if (!searchTerm || searchTerm.trim() === "") {
    return [];
  }

  const api = new GhostContentAPI({
    url: import.meta.env.REACT_APP_GUIDES_GHOST_DOMAIN,
    key: import.meta.env.REACT_APP_GUIDES_GHOST_API_KEY,
    version: GUIDES_GHOST_API_VERSION,
  });

  // Split search term into individual words
  const words = searchTerm
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0);

  // Escape single quotes in each word for NQL filter
  const escapedWords = words.map((word) => word.replace(/'/g, "\\'"));

  // Build NQL filter: (all words in title) OR (all words in plaintext)
  const titleConditions = escapedWords.map((word) => `title:~'${word}'`).join("+");
  const plaintextConditions = escapedWords.map((word) => `plaintext:~'${word}'`).join("+");
  const filter = `(${titleConditions}),(${plaintextConditions})`;

  // Fetch all results (Ghost API max is 100 per request, so use recursive pagination)
  const fetchPage = (page) =>
    api.posts
      .browse({
        filter,
        limit: 100,
        page,
        fields: "title,canonical_url,feature_image,slug,id",
        include: "tags",
      })
      .then((posts) => {
        if (posts.meta && posts.meta.pagination && posts.meta.pagination.pages > page) {
          return fetchPage(page + 1).then((nextPosts) => [...posts, ...nextPosts]);
        }
        return [...posts];
      });

  return fetchPage(1);
};

/**
 * Executes Ghost API for guides posts.
 *
 * @param {String} slug
 * @return {Promise}
 * */
const fetchGhostPostBySlug = async (slug) => {
  const api = new GhostContentAPI({
    url: import.meta.env.REACT_APP_GUIDES_GHOST_DOMAIN,
    key: import.meta.env.REACT_APP_GUIDES_GHOST_API_KEY,
    version: GUIDES_GHOST_API_VERSION,
  });

  return api.posts
    .read({ slug, include: "tags" })
    .then((posts) => posts)
    .catch((err) => {
      logError(err);
      return [];
    });
};

/**
 * Generate sized image URL, add size/wxh after /images/
 * */
export const generateImageUrl = (imageUrl, imageSize = GHOST_IMAGE_SIZE) => {
  try {
    Boolean(new URL(imageUrl)); // validate URL

    const [first, ...rest] = imageUrl.split("images");
    const last = [...rest].join("images");
    const sizedParam = ["images", "size", imageSize].join("/");
    return [first, last].join(sizedParam);
  } catch (e) {
    logError(e);
    return imageUrl;
  }
};

export {
  executeGhostApi,
  executeGuidesGhostApi,
  executeBlogGhostApi,
  fetchGhostPostBySlug,
  searchGuidesGhostApi,
};
